import React from "react";
import { Helmet } from "react-helmet-async";

const docksDoorsSolutions = [
  {
    title: "Dock Levelers",
    desc: "Hydraulic and mechanical dock levelers for safe loading and unloading.",
    img: "/docks-doors/dock-leveler.jpg",
    applications: [
      "Bridges gap between dock and vehicle.",
      "Ensures safe cargo transfer.",
      "Supports heavy vehicle loading cycles.",
      "Reduces impact and equipment wear.",
    ],
  },
  {
    title: "Dock Shelters",
    desc: "Flexible dock shelters for efficient sealing and protection from the elements.",
    img: "/docks-doors/dock-shelters.jpg",
    applications: [
      "Provides weather sealing during loading.",
      "Reduces energy loss in temperature-controlled areas.",
      "Protects goods and personnel from rain and dust.",
      "Compatible with various vehicle sizes.",
    ],
  },
  {
    title: "Loading Houses",
    desc: "Pre-fabricated loading systems for efficient goods transfer.",
    img: "https://gw-assets.assaabloy.com/is/image/assaabloy/LH6081L_0001_16_9",
    applications: [
      "Integrated docking, shelter, and door in one module.",
      "Ideal for retrofit or space-limited sites.",
      "Reduces construction work and downtime.",
      "Energy-efficient and durable design.",
    ],
  },
  {
    title: "Control Systems",
    desc: "Smart controls for dock and door operations.",
    img: "/docks-doors/control-systems.jpg",
    applications: [
      "Integrates dock levelers, shelters, and doors.",
      "Ensures synchronized operation and safety.",
      "Can include sensors and interlocks.",
      "Supports automation and monitoring systems.",
    ],
  },
  {
    title: "Industrial Sectional Doors",
    desc: "Insulated, vertically opening doors for industrial and commercial facilities.",
    img: "/docks-doors/industrialsecdoor.jpg",
    applications: [
      "Provides thermal insulation and noise reduction.",
      "Operates vertically to save internal space.",
      "Ideal for warehouses, factories, and logistics hubs.",
      "Available in manual or automated operation.",
    ],
  },
  {
    title: "High-Speed Doors",
    desc: "Fast-operating sectional doors for high-traffic industrial environments.",
    img: "/docks-doors/high-speed-door.jpg",
    applications: [
      "Enhances workflow efficiency in busy areas.",
      "Maintains temperature control in sensitive zones.",
      "Equipped with safety sensors and quick-reset features.",
      "Reduces downtime and energy loss.",
    ],
  },
  {
    title: "High-Speed Sectional Doors / ISO Cold",
    desc: "Fast-operating sectional doors for high-traffic industrial environments.",
    img: "/docks-doors/iso-door.jpg",
    applications: [
      "Enhances workflow efficiency in busy areas.",
      "Maintains temperature control in sensitive zones.",
      "Equipped with safety sensors and quick-reset features.",
      "Reduces downtime and energy loss.",
    ],
  },
  {
    title: "Industrial Rolling Shutters / Rolling Grilles",
    desc: "Heavy-duty steel or aluminum shutters for secure and efficient closure.",
    img: "/docks-doors/rolling-grilles.jpg",
    applications: [
      "Provides robust protection for loading docks and entrances.",
      "Compact design saves space above the doorway.",
      "Can be operated manually or with motorized systems.",
      "Ideal for industrial and commercial buildings.",
    ],
  },

  // {
  //   title: "Strip Curtains",
  //   desc: "Flexible PVC curtains providing separation while allowing easy passage.",
  //   img: "https://www.stripcurtainsdirect.co.uk/cdn/shop/articles/what-are-pvc-strip-curtains.jpg",
  //   applications: [
  //     "Maintains temperature zones and reduces dust entry.",
  //     "Used in cold storage, food processing, and cleanrooms.",
  //     "Allows passage of personnel and equipment without obstruction.",
  //     "Simple to install and replace.",
  //   ],
  // },
  // {
  //   title: "ISO Doors",
  //   desc: "Specialized doors designed for temperature-controlled and cleanroom environments.",
  //   img: "https://www.metaflexdoors.com/wp-content/uploads/2021/06/ISO-262_3-scaled.jpg",
  //   applications: [
  //     "Ensures airtight sealing and insulation.",
  //     "Used in pharmaceutical, food, and cold storage facilities.",
  //     "Supports hygiene standards and contamination control.",
  //     "Available in sliding or hinged configurations.",
  //   ],
  // },
];

const DocksDoors = () => (
  <div className="flex flex-col gap-12">
    <Helmet>
      <title>Docks & Doors | Prime Sales Incorporated</title>
      <meta
        name="description"
        content="Prime Sales Incorporated offers efficient dock levelers, industrial doors, and loading systems for safe, fast, and reliable warehouse operations in the Philippines."
      />
      <link
        rel="canonical"
        href="https://www.primegroup.com.ph/solutions/docks-and-doors"
      />
    </Helmet>
    <div className="text-center mb-12">
      <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-white">
        Docks & Doors – Efficient Loading & Industrial Access Systems
      </h1>
      <p className="mt-2 text-gray-600 dark:text-gray-300 text-sm md:text-base max-w-4xl mx-auto">
        Prime Sales Incorporated offers efficient dock levelers, industrial
        doors, and loading systems for safe, fast, and reliable warehouse
        operations in the Philippines.
      </p>
    </div>

    {/* Desktop: original alternating big-image layout. Mobile: compact list row like a shopping app. */}
    <div className="flex flex-col gap-5 md:gap-12">
      {docksDoorsSolutions.map((solution, index) => (
        <div
          key={index}
          className={`flex flex-row md:items-center gap-4 md:gap-0 rounded-2xl overflow-hidden border border-primary/20 dark:border-primary/30
          bg-background-light dark:bg-background-dark shadow-sm md:shadow-md hover:shadow-lg transition p-3 md:p-0
          md:flex-row ${index % 2 === 1 ? "md:flex-row-reverse" : ""}`}
        >
          {/* Thumbnail: small square on mobile, large half-width image on desktop */}
          <div
            className="w-20 h-20 shrink-0 rounded-lg bg-center bg-cover
            md:w-1/2 md:h-56 md:rounded-none sm:md:h-64 md:h-[18em]"
            style={{ backgroundImage: `url(${solution.img})` }}
          ></div>

          <div className="flex-1 min-w-0 text-left md:w-1/2 md:p-6 md:sm:p-8 md:text-center md:md:text-left">
            <h3 className="text-sm sm:text-base md:text-2xl font-bold text-gray-900 dark:text-white mb-1 md:mb-3 truncate md:whitespace-normal">
              {solution.title}
            </h3>
            <p className="hidden md:block text-gray-600 dark:text-gray-300 text-sm md:text-base leading-relaxed max-w-xl mx-auto md:mx-0 mb-4">
              {solution.desc}
            </p>
            <p className="md:hidden text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
              {solution.desc}
            </p>

            {/* Applications: bullet lines (compact preview on mobile, full list on desktop) */}
            <div className="mt-2 md:mt-4">
              <h4 className="hidden md:block text-lg font-semibold text-green-500 mb-2">
                Applications
              </h4>
              <ul className="hidden md:block list-disc pl-5 text-sm md:text-base text-gray-600 dark:text-gray-300 space-y-1">
                {solution.applications.map((app, i) => (
                  <li key={i}>{app}</li>
                ))}
              </ul>
              {/* Compact mobile bullet preview */}
              <ul className="md:hidden flex flex-col gap-1 mt-1.5">
                {solution.applications.slice(0, 2).map((app, i) => (
                  <li
                    key={i}
                    className="text-[11px] text-gray-500 dark:text-gray-400 truncate flex items-center gap-1"
                  >
                    <span className="w-1 h-1 rounded-full bg-green-500 shrink-0" />
                    {app}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default DocksDoors;
